'use client';

import React, { useMemo } from 'react';
import { useGetStrategyMetricsQuery } from 'src/api/redux/strategy';
import {
  useGetObjectDefinitionsListQuery,
  useGetFieldDefinitionsQuery,
} from 'src/api/redux/medrm';
import { MetricItem } from './components';
import { StrategyExecutionSummary } from '../StrategyExecutionSummary';
interface StrategyMetricEditingProps {
  strategyId: string;
  strategyType: 'target' | 'engage';
}

// Base columns that are always shown
const baseColumns = [
  {
    label: 'Name',
    description: 'Name of site or provider',
    disabled: true,
  },
  {
    label: 'Status',
    description: 'CRM Status',
    disabled: true,
  },
  {
    label: 'Specialty',
    description: 'Specialty',
    disabled: true,
  },
  {
    label: 'City',
    description: 'City',
    disabled: true,
  },
  {
    label: 'Lists',
    description: 'Lists that include the provider',
    disabled: true,
  },
  {
    label: 'Date Added',
    description: 'Date added',
    disabled: true,
  },
];

const StrategyMetricEditing: React.FC<StrategyMetricEditingProps> = ({
  strategyId,
  strategyType,
}) => {
  // Fetch data from API
  const { data: apiData, isLoading } = useGetStrategyMetricsQuery(
    { strategyType, strategyId },
    { skip: !strategyId }
  );

  // Fetch MedRM field definitions to map status values to user-configured labels
  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery();
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
    { objectId: objectDefinitions?.results?.[0]?.id },
    { skip: !objectDefinitions?.results?.[0]?.id }
  );

  const statusLabelMap = useMemo(() => {
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(
      statusField.options.map((opt) => [opt.value, opt.label])
    );
  }, [fieldDefinitions]);

  // Combine base columns with API data
  const allColumns = useMemo(() => {
    if (!apiData?.results) {
      return baseColumns;
    }

    return [
      ...baseColumns.slice(0, 4),
      ...apiData.results,
      ...baseColumns.slice(4),
    ];
  }, [apiData]);

  if (isLoading) {
    return (
      <div
        className="flex flex-col gap-6 w-full"
        data-testid="strategy-metric-editing-loading"
        aria-busy="true"
        aria-label="Loading column data"
      >
        <div className="flex flex-col gap-1">
          <h2 className="justify-start text-primary text-xl font-bold font-['Inter'] leading-7">
            Column Names
          </h2>
          <p className="self-stretch justify-start text-teritary text-sm font-normal font-['Inter'] leading-tight">
            Loading column data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <section
      className="flex flex-col gap-4 mx-auto w-full"
      data-testid="strategy-metric-editing"
      aria-label="Strategy metric column editing"
    >
      <div className="flex flex-col gap-6 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col gap-1">
          <h2 className="justify-start text-primary text-xl font-bold font-['Inter'] leading-7">
            Column Names
          </h2>
          <p className="self-stretch justify-start text-teritary text-sm font-normal font-['Inter'] leading-tight">
            Edit the names of the columns displayed for List view. Changes save
            automatically.
          </p>
        </div>

        <ul
          className="flex flex-col gap-4 lg:gap-6 w-full"
          data-testid="metric-columns-list"
          aria-label="Metric columns"
          role="list"
        >
          {allColumns.length > 0 ? (
            allColumns.map((item, index) => (
              <MetricItem
                key={(item as any).id || index}
                item={item}
                strategyId={strategyId}
                statusLabelMap={statusLabelMap}
              />
            ))
          ) : (
            <li className="text-slate-500 text-sm">Loading columns...</li>
          )}
        </ul>
      </div>
      <StrategyExecutionSummary strategyType={strategyType} />
    </section>
  );
};

export default StrategyMetricEditing;
